import { decide } from '@emr/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { createLicenseScanToken, revokeLicenseScanToken } from '../../api/licenseScan.ts';
import { errorMessage } from '../../api/client.ts';
import { useGrants } from '../../auth/sessionContext.ts';
import { Button, ErrorText, Panel } from '../../components/primitives.tsx';

/**
 * Staff-facing entry point for a driver's-license barcode scan (`BACKLOG.md` C19,
 * `docs/decisions/patient-license-scan-scope.md`): start a token-scoped capture session for this
 * patient, hand the resulting link to a tablet, revoke it early if needed. Embedded into
 * `PatientChartPage.tsx` alongside `KioskTokenPanel.tsx`, matching that panel's own placement
 * reasoning exactly — a front-desk entry point, not clinical chart content.
 *
 * Gated on `patient:read`, matching `licenseScan/routes.ts`'s own server-side capability choice and
 * `kiosk/routes.ts`'s identical reasoning for its own token pair: issuing a token does not itself
 * write to the patient record.
 */
export function LicenseScanTokenPanel({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('licenseScan');
  const grants = useGrants();
  const [active, setActive] = useState<{ id: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () => createLicenseScanToken(patientId),
    onSuccess: (response) => {
      const { id, token } = response.token;
      if (token !== undefined) setActive({ id, token });
      setCopied(false);
    },
  });
  const revoke = useMutation({
    mutationFn: (tokenId: string) => revokeLicenseScanToken(tokenId),
    onSuccess: () => {
      setActive(null);
    },
  });

  if (!decide(grants, 'patient:read')) return null;

  const link =
    active !== null ? `${window.location.origin}/kiosk/license-scan#token=${active.token}` : null;

  return (
    <Panel title={t('staffPanelTitle')} className="mb-4">
      {active === null ? (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-sm text-ink-muted">{t('staffPanelBody')}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => {
                create.mutate();
              }}
              pending={create.isPending}
              className="self-start"
            >
              {t('staffCreateButton')}
            </Button>
            <Link
              to={`/patients/${patientId}/license-scans`}
              className="text-sm font-medium text-accent-text underline-offset-2 hover:underline"
            >
              {t('reviewQueueLink')}
            </Link>
          </div>
          {create.isError ? <ErrorText message={errorMessage(create.error)} /> : null}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-sm text-ink">{t('staffLinkReady')}</p>
          <code className="block rounded-control border border-line bg-field p-2 text-xs break-all text-ink">
            {link}
          </code>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                if (link === null) return;
                navigator.clipboard
                  .writeText(link)
                  .then(() => {
                    setCopied(true);
                  })
                  .catch(() => {
                    // Clipboard access can be refused by browser policy — the link is already
                    // visible and selectable in the <code> block above, so this is a convenience,
                    // not the only way to get it.
                  });
              }}
            >
              {copied ? t('staffCopiedButton') : t('staffCopyButton')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                revoke.mutate(active.id);
              }}
              pending={revoke.isPending}
            >
              {t('staffRevokeButton')}
            </Button>
          </div>
          {revoke.isError ? <ErrorText message={errorMessage(revoke.error)} /> : null}
        </div>
      )}
    </Panel>
  );
}
